import {
  getLocalDateFromDateTime,
  getFirstDayOfTheWeek,
} from '@wix/bookings-catalog-calendar-viewer-utils/dateAndTime';
import { WidgetViewModelFactory } from '../viewModel';
import { AsyncComponentDataStatus } from '../../state/types';
import { ViewModelSubscription } from '../initViewModelSubscriptions';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { LanguageSettings } from '@wix/bookings-catalog-calendar-viewer-utils';

export type DatePickerViewModel = {
  selectedDate?: string;
  availableDates: string[];
  locale: string;
  today: string;
  firstDayOfTheWeek: number;
  accessibility: {
    nextAriaLabel: string;
    prevAriaLabel: string;
  };
};

export const createDatePickerViewModel: WidgetViewModelFactory<
  DatePickerViewModel
> = ({ state: { selectedDate, languageSettings, availableDays }, flowAPI }) => {
  const {
    translations: { t },
    environment: { isEditor },
  } = flowAPI;
  if (
    isEditor &&
    availableDays.status === AsyncComponentDataStatus.IDLE &&
    !availableDays.data.length
  ) {
    return createDummyDatePickerViewModel({ flowAPI, languageSettings });
  }

  const today = new Date().toISOString().split('T')[0];
  const firstDayOfTheWeek = getFirstDayOfTheWeek(
    languageSettings.dateRegionalSettingsLocale,
  );

  const accessibility = {
    nextAriaLabel: t('app.week-picker.accessibility.next-week'),
    prevAriaLabel: t('app.week-picker.accessibility.prev-week'),
  };

  return {
    selectedDate: getLocalDateFromDateTime(selectedDate),
    locale: languageSettings.dateRegionalSettingsLocale,
    availableDates:
      availableDays.status === AsyncComponentDataStatus.IDLE
        ? availableDays.data
        : [],
    today,
    firstDayOfTheWeek,
    accessibility,
  };
};

export const subscribeToDatePickerViewModel: ViewModelSubscription = ({
  subscribe,
  setViewModel,
  flowAPI,
  getState,
}) =>
  subscribe(
    ({ selectedDate, availableDays }) => ({
      selectedDate,
      availableDaysStatus: availableDays.status,
    }),
    () => {
      const datePickerViewModel = createDatePickerViewModel({
        flowAPI,
        state: getState(),
      });
      setViewModel({
        datePickerViewModel,
      });
    },
  );

export const createDummyDatePickerViewModel = ({
  flowAPI: {
    translations: { t },
  },
  languageSettings,
}: {
  flowAPI: ControllerFlowAPI;
  languageSettings: LanguageSettings;
}): DatePickerViewModel => {
  const firstDayOfTheWeek = getFirstDayOfTheWeek(
    languageSettings.dateRegionalSettingsLocale,
  );

  const today = new Date();
  const tommorow = new Date(today);
  tommorow.setDate(today.getDate() + 1);

  return {
    selectedDate: today.toISOString().split('T')[0],
    availableDates: [
      today.toISOString().split('T')[0],
      tommorow.toISOString().split('T')[0],
    ],
    locale: languageSettings.dateRegionalSettingsLocale,
    today: today.toISOString().split('T')[0],
    firstDayOfTheWeek,
    accessibility: {
      nextAriaLabel: t('app.week-picker.accessibility.next-week'),
      prevAriaLabel: t('app.week-picker.accessibility.prev-week'),
    },
  };
};
